import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../components/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [data, setData] = useState(null);
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    let attempts = 0;
    const max = 8;

    const poll = async () => {
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        setData(res.data);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
        if (res.data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts >= max) {
          setStatus("timeout");
          return;
        }
        attempts++;
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= max) {
          setStatus("error");
          return;
        }
        attempts++;
        setTimeout(poll, 2000);
      }
    };

    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <section
      data-testid="page-payment-success"
      className="container-fld py-32 min-h-[60vh] flex items-center justify-center"
    >
      <div className="max-w-2xl w-full tactical-card p-12 text-center">
        {status === "polling" && (
          <>
            <Loader2 className="w-16 h-16 text-[#7CFF00] mx-auto mb-6 animate-spin" />
            <div className="overline mb-3">// Verifying Payment</div>
            <h1 className="font-tactical text-3xl md:text-4xl font-bold uppercase text-white tracking-tight">
              Confirming your order
            </h1>
            <p className="mt-4 text-zinc-400">Hold position. This takes a moment.</p>
          </>
        )}

        {status === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-[#7CFF00] mx-auto mb-6" />
            <div className="overline mb-3">// Order Confirmed</div>
            <h1 className="font-tactical text-3xl md:text-5xl font-bold uppercase text-white tracking-tight">
              Mission Accomplished.
            </h1>
            <p className="mt-4 text-zinc-400 leading-relaxed">
              Your gear is locked in. Total charged:{" "}
              <span className="text-[#7CFF00] font-mono-tactical">
                ${(data?.amount_total / 100).toFixed(2)}
              </span>
              . You'll receive a confirmation email shortly.
            </p>
            <Link to="/" className="btn-primary mt-8" data-testid="payment-home-cta">
              Return Home <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}

        {(status === "expired" || status === "error" || status === "timeout") && (
          <>
            <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-6" />
            <div className="overline mb-3">// Status Unclear</div>
            <h1 className="font-tactical text-3xl md:text-4xl font-bold uppercase text-white tracking-tight">
              We couldn't verify the order
            </h1>
            <p className="mt-4 text-zinc-400">
              Don't worry — if you were charged, we'll spot it. Reach out and
              we'll handle it personally.
            </p>
            <div className="mt-8 flex gap-3 justify-center flex-wrap">
              <Link to="/cart" className="btn-secondary">
                Back to Cart
              </Link>
              <Link to="/contact" className="btn-primary">
                Contact Us
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
